import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuIcon from '@mui/icons-material/Menu';
import HighlightIcon from '@mui/icons-material/Highlight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridViewIcon from '@mui/icons-material/GridView';
import PrintIcon from '@mui/icons-material/Print';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import CropFreeIcon from '@mui/icons-material/CropFree';
import GetAppIcon from '@mui/icons-material/GetApp';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { config } from '../config/environment';
import { caseAPI } from '../services/api';

// PDF.js worker (react-pdf v10)
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export interface DocumentBookmark {
  id: number;
  case_file_id: number;
  page_number: number;
  title: string;
  sort_order: number;
  x?: number | null;
  y?: number | null;
  width?: number | null;
  height?: number | null;
}

export interface DocumentRedaction {
  id: number;
  case_file_id: number;
  page_number: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

const SIDEBAR_WIDTH = 280;
const PAGE_WIDTH_DEFAULT = 720;
/** Only render this many pages above/below current to keep heavy PDFs responsive */
const PAGE_RENDER_MARGIN = 2;
/** A4 aspect ratio for placeholder height */
const PAGE_ASPECT = 297 / 210;

const DocumentReview: React.FC = () => {
  const { firmId, caseId, fileId } = useParams<{ firmId: string; caseId: string; fileId: string }>();
  const navigate = useNavigate();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [pdfPagesLoaded, setPdfPagesLoaded] = useState(false);
  const [firstPageRendered, setFirstPageRendered] = useState(false);
  const firstPageRenderedRef = useRef(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [importingOutlines, setImportingOutlines] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [bookmarks, setBookmarks] = useState<DocumentBookmark[]>([]);
  const [redactions, setRedactions] = useState<DocumentRedaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addRedactionMode, setAddRedactionMode] = useState(false);
  const [editingBookmark, setEditingBookmark] = useState<DocumentBookmark | null>(null);
  const [bookmarkTitle, setBookmarkTitle] = useState('');
  const [drawing, setDrawing] = useState<{ page: number; startX: number; startY: number; currentX: number; currentY: number } | null>(null);
  const [drawingBookmarkRegion, setDrawingBookmarkRegion] = useState<DocumentBookmark | null>(null);
  const [addingBookmarkHighlight, setAddingBookmarkHighlight] = useState(false);
  const [pendingNewBookmarkRegion, setPendingNewBookmarkRegion] = useState<{ page_number: number; x: number; y: number; width: number; height: number } | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [highlightBookmarkId, setHighlightBookmarkId] = useState<number | null>(null);
  const [draggingRedaction, setDraggingRedaction] = useState<{
    redaction: DocumentRedaction;
    offsetX: number;
    offsetY: number;
    startMouseX: number;
    startMouseY: number;
    pageRect: DOMRect;
  } | null>(null);
  const [resizingRedaction, setResizingRedaction] = useState<{
    redaction: DocumentRedaction;
    startMouseX: number;
    startMouseY: number;
    startWidth: number;
    startHeight: number;
    pageRect: DOMRect;
  } | null>(null);
  const [resizingBookmark, setResizingBookmark] = useState<{
    bookmark: DocumentBookmark;
    startMouseX: number;
    startMouseY: number;
    startWidth: number;
    startHeight: number;
    pageRect: DOMRect;
  } | null>(null);
  const [draggingBookmark, setDraggingBookmark] = useState<{
    bookmark: DocumentBookmark;
    offsetX: number;
    offsetY: number;
    startMouseX: number;
    startMouseY: number;
    pageRect: DOMRect;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefsMap = useRef<Record<number, HTMLDivElement | null>>({});
  const highlightRegionRef = useRef<HTMLDivElement>(null);
  const draggingRedactionPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const resizingRedactionSizeRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });
  const resizingBookmarkSizeRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });
  const draggingBookmarkPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const cId = caseId ? parseInt(caseId, 10) : 0;
  const fId = fileId ? parseInt(fileId, 10) : 0;

  // Memoize lookups by page so we don't filter redactions/bookmarks for every page on every render (heavy PDFs)
  const redactionsByPage = useMemo(() => {
    const map: Record<number, DocumentRedaction[]> = {};
    redactions.forEach((r) => {
      if (!map[r.page_number]) map[r.page_number] = [];
      map[r.page_number].push(r);
    });
    return map;
  }, [redactions]);

  const bookmarksWithRegionByPage = useMemo(() => {
    const map: Record<number, DocumentBookmark[]> = {};
    bookmarks.forEach((b) => {
      if (b.x != null && b.y != null && b.width != null && b.height != null) {
        if (!map[b.page_number]) map[b.page_number] = [];
        map[b.page_number].push(b);
      }
    });
    return map;
  }, [bookmarks]);

  const loadPdfBlob = useCallback(async () => {
    if (!cId || !fId) return;
    const token = localStorage.getItem('token');
    const url = `${config.apiBaseUrl}/cases/${cId}/files/${fId}/download`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error('Failed to load PDF');
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  }, [cId, fId]);

  const loadReviewData = useCallback(async () => {
    if (!cId || !fId) return;
    const res = await caseAPI.getDocumentReview(cId, fId);
    if (res.data?.success && res.data?.data) {
      const bookmarksList = res.data.data.bookmarks || [];
      const redactionsList = res.data.data.redactions || [];
      setBookmarks(bookmarksList);
      setRedactions(redactionsList);
      if (bookmarksList.length === 0) {
        try {
          const importRes = await caseAPI.importDocumentReviewOutlines(cId, fId);
          if (importRes.data?.success && importRes.data?.data?.imported_count > 0 && importRes.data?.data?.bookmarks) {
            setBookmarks(importRes.data.data.bookmarks);
          }
        } catch {
          // PDF may have no outline or import failed; keep empty bookmarks
        }
      }
    }
  }, [cId, fId]);

  useEffect(() => {
    let objectUrl: string | null = null;
    const run = async () => {
      setError(null);
      setLoading(true);
      try {
        const url = await loadPdfBlob();
        if (url) {
          objectUrl = url;
          setPdfUrl(url);
          setPdfPagesLoaded(false);
          await loadReviewData();
        } else {
          setError('Invalid document');
        }
      } catch (e: any) {
        setError(e.message || 'Failed to load document');
      } finally {
        setLoading(false);
      }
    };
    run();
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [loadPdfBlob, loadReviewData]);

  const refreshReviewData = useCallback(() => {
    loadReviewData();
  }, [loadReviewData]);

  const handleImportOutlines = useCallback(async () => {
    if (!cId || !fId) return;
    setImportingOutlines(true);
    try {
      const res = await caseAPI.importDocumentReviewOutlines(cId, fId);
      if (res.data?.success && res.data?.data?.bookmarks != null) {
        setBookmarks(res.data.data.bookmarks);
      }
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to import PDF outline');
    } finally {
      setImportingOutlines(false);
    }
  }, [cId, fId]);

  const handleAddBookmarkFromRegion = async () => {
    const title = bookmarkTitle.trim() || `Page ${currentPage}`;
    if (!pendingNewBookmarkRegion) return;
    const { page_number, x, y, width, height } = pendingNewBookmarkRegion;
    try {
      const res = await caseAPI.createBookmark(cId, fId, {
        page_number,
        title,
        sort_order: bookmarks.length,
        x,
        y,
        width,
        height,
      });
      if (res.data?.success && res.data?.data) {
        setBookmarks((prev) => [...prev, res.data.data].sort((a, b) => a.sort_order - b.sort_order || a.page_number - b.page_number));
        setBookmarkTitle('');
        setPendingNewBookmarkRegion(null);
      }
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to add bookmark');
    }
  };

  const handleUpdateBookmark = async () => {
    if (!editingBookmark) return;
    try {
      const res = await caseAPI.updateBookmark(cId, fId, editingBookmark.id, {
        title: bookmarkTitle.trim(),
        page_number: editingBookmark.page_number,
        sort_order: editingBookmark.sort_order,
      });
      if (res.data?.success && res.data?.data) {
        setBookmarks((prev) => prev.map((b) => (b.id === editingBookmark.id ? res.data.data : b)));
        setEditingBookmark(null);
        setBookmarkTitle('');
      }
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to update bookmark');
    }
  };

  const handleGeneratePdf = async () => {
    if (!cId || !fId) return;
    setGeneratingPdf(true);
    setError(null);
    try {
      const res = await caseAPI.generateDocumentReviewPdf(cId, fId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `document-review-${fId}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleDeleteBookmark = async (b: DocumentBookmark) => {
    try {
      await caseAPI.deleteBookmark(cId, fId, b.id);
      setBookmarks((prev) => prev.filter((x) => x.id !== b.id));
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to delete bookmark');
    }
  };

  const handleDeleteRedaction = async (r: DocumentRedaction) => {
    if (r.id < 0) {
      setRedactions((prev) => prev.filter((x) => x.id !== r.id));
      return;
    }
    try {
      await caseAPI.deleteRedaction(cId, fId, r.id);
      setRedactions((prev) => prev.filter((x) => x.id !== r.id));
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to delete redaction');
    }
  };

  const handlePageMouseDown = (pageNum: number, e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    if (draggingRedaction) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (addingBookmarkHighlight || drawingBookmarkRegion) {
      setDrawing({ page: pageNum, startX: x, startY: y, currentX: x, currentY: y });
      return;
    }
    if (!addRedactionMode) return;
    setDrawing({ page: pageNum, startX: x, startY: y, currentX: x, currentY: y });
  };

  const handlePageMouseMove = (pageNum: number, e: React.MouseEvent<HTMLDivElement>) => {
    if (!drawing || drawing.page !== pageNum) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setDrawing((prev) => prev ? { ...prev, currentX: e.clientX - rect.left, currentY: e.clientY - rect.top } : null);
  };

  const handlePageMouseUp = async (pageNum: number, e: React.MouseEvent<HTMLDivElement>) => {
    if (!drawing || drawing.page !== pageNum) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    const x1 = Math.min(drawing.startX, drawing.currentX);
    const y1 = Math.min(drawing.startY, drawing.currentY);
    const x2 = Math.max(drawing.startX, drawing.currentX);
    const y2 = Math.max(drawing.startY, drawing.currentY);
    const width = x2 - x1;
    const height = y2 - y1;
    setDrawing(null);
    const x = x1 / w;
    const y = y1 / h;
    const nw = width / w;
    const nh = height / h;
    if (nw < 0.002 || nh < 0.002) return;
    // Only add redaction when user has actually dragged (minimum size) – not on accidental click
    const minRedactionNorm = 0.02; // ~2% of page
    if (!addingBookmarkHighlight && !drawingBookmarkRegion && (nw < minRedactionNorm || nh < minRedactionNorm)) return;
    const nwClamp = Math.max(0.01, Math.min(1, nw));
    const nhClamp = Math.max(0.01, Math.min(1, nh));
    if (addingBookmarkHighlight) {
      setPendingNewBookmarkRegion({
        page_number: pageNum,
        x: Math.max(0, Math.min(1, x)),
        y: Math.max(0, Math.min(1, y)),
        width: Math.max(0.001, Math.min(1, nw)),
        height: Math.max(0.001, Math.min(1, nh)),
      });
      setAddingBookmarkHighlight(false);
      setBookmarkTitle('');
      return;
    }
    if (drawingBookmarkRegion) {
      try {
        const res = await caseAPI.updateBookmark(cId, fId, drawingBookmarkRegion.id, {
          x: Math.max(0, Math.min(1, x)),
          y: Math.max(0, Math.min(1, y)),
          width: Math.max(0.001, Math.min(1, nw)),
          height: Math.max(0.001, Math.min(1, nh)),
          page_number: pageNum,
        });
        if (res.data?.success && res.data?.data) {
          setBookmarks((prev) => prev.map((b) => (b.id === drawingBookmarkRegion.id ? res.data.data : b)));
        }
        setDrawingBookmarkRegion(null);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to set bookmark region');
      }
      return;
    }
    const tempId = -Date.now();
    const optimisticRedaction: DocumentRedaction = {
      id: tempId,
      case_file_id: fId,
      page_number: pageNum,
      x: Math.max(0, Math.min(1, x)),
      y: Math.max(0, Math.min(1, y)),
      width: nwClamp,
      height: nhClamp,
    };
    setRedactions((prev) => [...prev, optimisticRedaction]);
    try {
      const res = await caseAPI.createRedaction(cId, fId, {
        page_number: pageNum,
        x: optimisticRedaction.x,
        y: optimisticRedaction.y,
        width: nwClamp,
        height: nhClamp,
      });
      if (res.data?.success && res.data?.data) {
        setRedactions((prev) => prev.map((r) => (r.id === tempId ? res.data.data : r)));
      }
    } catch (err: any) {
      setRedactions((prev) => prev.filter((r) => r.id !== tempId));
      setError(err.response?.data?.message || 'Failed to add redaction');
    }
  };

  const handleMouseLeave = () => {
    setDrawing(null);
  };

  // Document-level listeners for dragging redactions
  useEffect(() => {
    if (!draggingRedaction) return;
    const { redaction, offsetX, offsetY, pageRect } = draggingRedaction;
    const handleMove = (e: MouseEvent) => {
      const newLeft = e.clientX - offsetX;
      const newTop = e.clientY - offsetY;
      const newX = Math.max(0, Math.min(1 - redaction.width, (newLeft - pageRect.left) / pageRect.width));
      const newY = Math.max(0, Math.min(1 - redaction.height, (newTop - pageRect.top) / pageRect.height));
      draggingRedactionPosRef.current = { x: newX, y: newY };
      setRedactions((prev) =>
        prev.map((r) => (r.id === redaction.id ? { ...r, x: newX, y: newY } : r))
      );
    };
    const handleUp = async (e: MouseEvent) => {
      const dx = e.clientX - draggingRedaction.startMouseX;
      const dy = e.clientY - draggingRedaction.startMouseY;
      const moved = Math.hypot(dx, dy) >= 5;
      setDraggingRedaction(null);
      if (moved) {
        const { x, y } = draggingRedactionPosRef.current;
        try {
          const res = await caseAPI.updateRedaction(cId, fId, redaction.id, { x, y });
          if (res.data?.success && res.data?.data) {
            setRedactions((prev) => prev.map((x) => (x.id === redaction.id ? res.data.data : x)));
          }
        } catch (err: any) {
          setError(err.response?.data?.message || 'Failed to move redaction');
        }
      } else if (addRedactionMode) {
        handleDeleteRedaction(redaction);
      }
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [draggingRedaction, addRedactionMode, cId, fId]);

  // Document-level listeners for resizing redactions
  useEffect(() => {
    if (!resizingRedaction) return;
    const { redaction, startMouseX, startMouseY, startWidth, startHeight, pageRect } = resizingRedaction;
    const handleMove = (e: MouseEvent) => {
      const dw = (e.clientX - startMouseX) / pageRect.width;
      const dh = (e.clientY - startMouseY) / pageRect.height;
      let newW = Math.max(0.02, Math.min(1 - redaction.x, startWidth + dw));
      let newH = Math.max(0.02, Math.min(1 - redaction.y, startHeight + dh));
      resizingRedactionSizeRef.current = { width: newW, height: newH };
      setRedactions((prev) =>
        prev.map((r) => (r.id === redaction.id ? { ...r, width: newW, height: newH } : r))
      );
    };
    const handleUp = async () => {
      const { width, height } = resizingRedactionSizeRef.current;
      setResizingRedaction(null);
      try {
        const res = await caseAPI.updateRedaction(cId, fId, redaction.id, { width, height });
        if (res.data?.success && res.data?.data) {
          setRedactions((prev) => prev.map((x) => (x.id === redaction.id ? res.data.data : x)));
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to resize redaction');
      }
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [resizingRedaction, cId, fId]);

  // Document-level listeners for resizing bookmark region
  useEffect(() => {
    if (!resizingBookmark) return;
    const { bookmark, startMouseX, startMouseY, startWidth, startHeight, pageRect } = resizingBookmark;
    const handleMove = (e: MouseEvent) => {
      const dw = (e.clientX - startMouseX) / pageRect.width;
      const dh = (e.clientY - startMouseY) / pageRect.height;
      const newW = Math.max(0.02, Math.min(1 - (bookmark.x ?? 0), startWidth + dw));
      const newH = Math.max(0.02, Math.min(1 - (bookmark.y ?? 0), startHeight + dh));
      resizingBookmarkSizeRef.current = { width: newW, height: newH };
      setBookmarks((prev) =>
        prev.map((b) => (b.id === bookmark.id ? { ...b, width: newW, height: newH } : b))
      );
    };
    const handleUp = async () => {
      const { width, height } = resizingBookmarkSizeRef.current;
      setResizingBookmark(null);
      try {
        await caseAPI.updateBookmark(cId, fId, bookmark.id, { width, height });
        loadReviewData();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to resize bookmark');
      }
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [resizingBookmark, cId, fId, loadReviewData]);

  // Document-level listeners for dragging bookmark position
  useEffect(() => {
    if (!draggingBookmark) return;
    const { bookmark, offsetX, offsetY, pageRect } = draggingBookmark;
    const w = bookmark.width ?? 0;
    const h = bookmark.height ?? 0;
    const handleMove = (e: MouseEvent) => {
      const newLeft = e.clientX - offsetX;
      const newTop = e.clientY - offsetY;
      const newX = Math.max(0, Math.min(1 - w, (newLeft - pageRect.left) / pageRect.width));
      const newY = Math.max(0, Math.min(1 - h, (newTop - pageRect.top) / pageRect.height));
      draggingBookmarkPosRef.current = { x: newX, y: newY };
      setBookmarks((prev) =>
        prev.map((b) => (b.id === bookmark.id ? { ...b, x: newX, y: newY } : b))
      );
    };
    const handleUp = (e: MouseEvent) => {
      const dx = e.clientX - draggingBookmark.startMouseX;
      const dy = e.clientY - draggingBookmark.startMouseY;
      const moved = Math.hypot(dx, dy) >= 5;
      const { x, y } = draggingBookmarkPosRef.current;
      setDraggingBookmark(null);
      if (moved) {
        caseAPI.updateBookmark(cId, fId, bookmark.id, { x, y }).then(() => loadReviewData()).catch((err: any) => {
          setError(err.response?.data?.message || 'Failed to move bookmark');
        });
      }
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [draggingBookmark, cId, fId, loadReviewData]);

  // Scroll to bookmark region when a bookmark with region is clicked
  useEffect(() => {
    if (!highlightBookmarkId || !containerRef.current) return;
    const b = bookmarks.find((x) => x.id === highlightBookmarkId);
    if (!b) return;
    const pageEl = pageRefsMap.current[b.page_number];
    if (pageEl) {
      pageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (b.x != null && b.y != null && b.width != null && b.height != null) {
        const t = setTimeout(() => {
          highlightRegionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
        const clearHighlight = setTimeout(() => setHighlightBookmarkId(null), 3000);
        return () => { clearTimeout(t); clearTimeout(clearHighlight); };
      }
    }
    const clearHighlight = setTimeout(() => setHighlightBookmarkId(null), 3000);
    return () => clearTimeout(clearHighlight);
  }, [highlightBookmarkId, bookmarks]);

  // Sync current page when user scrolls (page in view)
  useEffect(() => {
    if (!containerRef.current || numPages === 0) return;
    const container = containerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const pageNum = parseInt(entry.target.getAttribute('data-page-number') ?? '0', 10);
          if (pageNum >= 1) setCurrentPage(pageNum);
        });
      },
      { root: container, rootMargin: '-40% 0px -40% 0px', threshold: 0 }
    );
    for (let i = 1; i <= numPages; i++) {
      const el = pageRefsMap.current[i];
      if (el) {
        el.setAttribute('data-page-number', String(i));
        observer.observe(el);
      }
    }
    return () => observer.disconnect();
  }, [numPages]);

  if (loading || !pdfUrl) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const basePageWidth = sidebarOpen ? PAGE_WIDTH_DEFAULT : Math.min(1000, (typeof window !== 'undefined' ? window.innerWidth : 1000) - 48);
  const pageWidth = Math.round(basePageWidth * zoom);
  const THUMB_WIDTH_SMALL = 56;
  const RIGHT_SIDEBAR_WIDTH = 88;

  const goToPage = (target: number) => {
    setCurrentPage(target);
    pageRefsMap.current[target]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', bgcolor: '#f5f5f5' }}>
      {/* Top toolbar – document editor style */}
      <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', px: 0.5, py: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <IconButton onClick={() => navigate(firmId && caseId ? `/firms/${firmId}/cases/${caseId}` : '/cases')} size="small" title="Back">
            <ArrowBackIcon />
          </IconButton>
          <Tooltip title={sidebarOpen ? 'Hide thumbnails' : 'Show thumbnails'}>
            <Button
              size="small"
              startIcon={<GridViewIcon />}
              onClick={() => setSidebarOpen((o) => !o)}
              sx={{ minWidth: 'auto', bgcolor: sidebarOpen ? 'action.selected' : undefined }}
            >
              Thumbnails
            </Button>
          </Tooltip>
          <Tooltip title="Highlight an area, then add a bookmark">
            <Button size="small" startIcon={<HighlightIcon />} onClick={() => setAddingBookmarkHighlight(true)} sx={{ minWidth: 'auto' }}>
              Highlight
            </Button>
          </Tooltip>
          <Tooltip title="Add redaction (yellow). Move, resize, or delete from the page.">
            <Button
              size="small"
              startIcon={<BlockIcon />}
              onClick={() => setAddRedactionMode((v) => !v)}
              sx={{ minWidth: 'auto', bgcolor: addRedactionMode ? 'action.selected' : undefined }}
            >
              Redact
            </Button>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Generate PDF with bookmarks index and redactions">
            <Button
              size="small"
              startIcon={generatingPdf ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
              onClick={handleGeneratePdf}
              disabled={generatingPdf}
              sx={{ minWidth: 'auto' }}
            >
              Print
            </Button>
          </Tooltip>
        </Box>
      </Paper>

      {addingBookmarkHighlight && (
        <Alert severity="info" onClose={() => setAddingBookmarkHighlight(false)} sx={{ mx: 1 }}>
          Highlight the area on the page for this bookmark (draw a rectangle), then add the bookmark title in the next step.
        </Alert>
      )}
      {drawingBookmarkRegion && (
        <Alert severity="info" onClose={() => setDrawingBookmarkRegion(null)} sx={{ mx: 1 }}>
          Draw a rectangle on the page where this bookmark should point. Release the mouse to save the region.
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mx: 1 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, flexDirection: 'row' }}>
        {/* Left sidebar: bookmarks panel (always visible) */}
        <Paper
          elevation={0}
          sx={{
            order: 1,
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            overflow: 'hidden',
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>Bookmarks</Typography>
            <Button size="small" variant="outlined" fullWidth startIcon={<HighlightIcon />} onClick={() => setAddingBookmarkHighlight(true)} sx={{ mb: 0.5 }}>
              Add bookmark
            </Button>
            <Tooltip title="Import bookmark outline from the PDF (adds to existing list)">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  fullWidth
                  startIcon={importingOutlines ? <CircularProgress size={14} color="inherit" /> : <GetAppIcon />}
                  onClick={handleImportOutlines}
                  disabled={importingOutlines}
                  sx={{ mb: 1 }}
                >
                  Import PDF outline
                </Button>
              </span>
            </Tooltip>
            <List dense sx={{ flex: 1, minHeight: 0 }}>
              {bookmarks.map((b) => (
                <ListItem
                  key={b.id}
                  sx={{ py: 0.25, bgcolor: b.page_number === currentPage ? 'action.selected' : undefined, borderRadius: 1 }}
                  secondaryAction={
                    <ListItemSecondaryAction>
                      <IconButton size="small" onClick={() => { setEditingBookmark(b); setBookmarkTitle(b.title); }}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => handleDeleteBookmark(b)}><DeleteIcon fontSize="small" /></IconButton>
                    </ListItemSecondaryAction>
                  }
                >
                  <ListItemText
                    primary={b.title}
                    secondary={`Page ${b.page_number}${b.x != null ? ' · has region' : ''}`}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                    onClick={() => {
                      setCurrentPage(b.page_number);
                      setHighlightBookmarkId(b.id);
                      setEditingBookmark(b);
                      setBookmarkTitle(b.title);
                      pageRefsMap.current[b.page_number]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    sx={{ cursor: 'pointer' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Paper>

        {/* Main PDF viewer + bottom floating bar */}
        <Box sx={{ order: 2, flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
        <Box ref={containerRef} sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0, position: 'relative' }}>
          {(!pdfPagesLoaded || !firstPageRendered) && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                bgcolor: 'background.paper',
                zIndex: 10,
              }}
            >
              <CircularProgress size={48} />
              <Typography variant="body2" color="text.secondary">
                {!pdfPagesLoaded ? 'Loading PDF…' : 'Rendering pages…'}
              </Typography>
            </Box>
          )}
          <Document
            file={pdfUrl}
            onLoadSuccess={({ numPages: n }) => {
              setNumPages(n);
              setPdfPagesLoaded(true);
              if (n === 0) {
                setFirstPageRendered(true);
                firstPageRenderedRef.current = true;
              } else {
                setFirstPageRendered(false);
                firstPageRenderedRef.current = false;
              }
            }}
            onLoadError={() => setPdfPagesLoaded(true)}
            loading={null}
          >
            {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => {
              const pageRedactions = redactionsByPage[pageNum] ?? [];
              const isDrawingThis = drawing?.page === pageNum;
              const renderWindowStart = Math.max(1, currentPage - PAGE_RENDER_MARGIN);
              const renderWindowEnd = Math.min(numPages, currentPage + PAGE_RENDER_MARGIN);
              const shouldRenderPage = pageNum >= renderWindowStart && pageNum <= renderWindowEnd;
              const placeholderHeight = pageWidth * PAGE_ASPECT;
              const isDrawingMode = addRedactionMode || addingBookmarkHighlight || !!drawingBookmarkRegion || !!drawing;
              return (
                <Box
                  key={pageNum}
                  ref={(el: HTMLDivElement | null) => { pageRefsMap.current[pageNum] = el; }}
                  sx={{
                    position: 'relative',
                    marginBottom: 2,
                    border: '1px solid',
                    borderColor: currentPage === pageNum ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    ...(shouldRenderPage ? {} : { minHeight: placeholderHeight, bgcolor: 'grey.100' }),
                  }}
                  onMouseDown={(e) => handlePageMouseDown(pageNum, e)}
                  onMouseMove={(e) => handlePageMouseMove(pageNum, e)}
                  onMouseUp={(e) => handlePageMouseUp(pageNum, e)}
                  onMouseLeave={handleMouseLeave}
                >
                  {shouldRenderPage ? (
                    <>
                  <Page
                    pageNumber={pageNum}
                    width={pageWidth}
                    onClick={() => setCurrentPage(pageNum)}
                    renderTextLayer={!isDrawingMode}
                    renderAnnotationLayer={!isDrawingMode}
                    onRenderSuccess={() => {
                      if (!firstPageRenderedRef.current) {
                        firstPageRenderedRef.current = true;
                        setFirstPageRendered(true);
                      }
                    }}
                  />
                  {/* Redaction overlays + bookmark highlight - fill the page container */}
                  <Box
                    data-page-overlay
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 2,
                      pointerEvents: 'auto',
                      cursor: (drawingBookmarkRegion || addingBookmarkHighlight) ? 'crosshair' : addRedactionMode ? 'crosshair' : 'default',
                    }}
                  >
                    {pageRedactions.map((r) => (
                      <Box
                        key={r.id}
                        title="Drag to move, drag corner to resize, or click delete to remove"
                        onMouseDown={(e) => {
                          if (e.button !== 0) return;
                          if (r.id < 0) return;
                          if ((e.target as HTMLElement).closest('[data-redaction-delete]') || (e.target as HTMLElement).closest('[data-redaction-resize]')) return;
                          e.stopPropagation();
                          e.preventDefault();
                          const overlay = e.currentTarget.parentElement;
                          if (!overlay) return;
                          const pageRect = overlay.getBoundingClientRect();
                          const boxRect = e.currentTarget.getBoundingClientRect();
                          setDraggingRedaction({
                            redaction: r,
                            offsetX: e.clientX - boxRect.left,
                            offsetY: e.clientY - boxRect.top,
                            startMouseX: e.clientX,
                            startMouseY: e.clientY,
                            pageRect,
                          });
                          draggingRedactionPosRef.current = { x: r.x, y: r.y };
                        }}
                        sx={{
                          position: 'absolute',
                          left: `${r.x * 100}%`,
                          top: `${r.y * 100}%`,
                          width: `${r.width * 100}%`,
                          height: `${r.height * 100}%`,
                          backgroundColor: 'rgba(255, 252, 220, 0.18)',
                          border: '1px solid rgba(255, 193, 7, 0.6)',
                          pointerEvents: 'auto',
                          cursor: 'move',
                          boxSizing: 'border-box',
                        }}
                      >
                        <IconButton
                          data-redaction-delete
                          size="small"
                          onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleDeleteRedaction(r); }}
                          onMouseDown={(e) => e.stopPropagation()}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            zIndex: 3,
                            padding: 0.35,
                            bgcolor: 'rgba(255,255,255,0.95)',
                            color: '#c62828',
                            boxShadow: 1,
                            '&:hover': { bgcolor: '#ffebee', color: '#b71c1c' },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                        <Box
                          data-redaction-resize
                          onMouseDown={(e) => {
                            if (e.button !== 0) return;
                            if (r.id < 0) return;
                            e.stopPropagation();
                            e.preventDefault();
                            const overlay = e.currentTarget.closest('[data-page-overlay]');
                            if (!overlay) return;
                            setResizingRedaction({
                              redaction: r,
                              startMouseX: e.clientX,
                              startMouseY: e.clientY,
                              startWidth: r.width,
                              startHeight: r.height,
                              pageRect: overlay.getBoundingClientRect(),
                            });
                            resizingRedactionSizeRef.current = { width: r.width, height: r.height };
                          }}
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            width: 14,
                            height: 14,
                            cursor: 'nwse-resize',
                            borderLeft: '2px solid rgba(0,0,0,0.5)',
                            borderTop: '2px solid rgba(0,0,0,0.5)',
                            boxSizing: 'border-box',
                          }}
                        />
                      </Box>
                    ))}
                    {/* Bookmark regions: show all bookmarks that have a region on this page; all are draggable and resizable */}
                    {(bookmarksWithRegionByPage[pageNum] ?? []).map((b) => {
                        const isSelected = b.id === highlightBookmarkId;
                        return (
                          <Box
                            key={b.id}
                            ref={isSelected ? highlightRegionRef : undefined}
                            title={`${b.title} — drag to move, drag corner to resize`}
                            onMouseDown={(e) => {
                              if (e.button !== 0) return;
                              if ((e.target as HTMLElement).closest('[data-bookmark-resize]')) return;
                              e.stopPropagation();
                              e.preventDefault();
                              const overlay = e.currentTarget.closest('[data-page-overlay]');
                              if (!overlay) return;
                              const boxRect = e.currentTarget.getBoundingClientRect();
                              setDraggingBookmark({
                                bookmark: b,
                                offsetX: e.clientX - boxRect.left,
                                offsetY: e.clientY - boxRect.top,
                                startMouseX: e.clientX,
                                startMouseY: e.clientY,
                                pageRect: overlay.getBoundingClientRect(),
                              });
                              draggingBookmarkPosRef.current = { x: b.x ?? 0, y: b.y ?? 0 };
                            }}
                            sx={{
                              position: 'absolute',
                              left: `${(b.x ?? 0) * 100}%`,
                              top: `${(b.y ?? 0) * 100}%`,
                              width: `${(b.width ?? 0) * 100}%`,
                              height: `${(b.height ?? 0) * 100}%`,
                              border: isSelected ? '3px solid' : '2px solid',
                              borderColor: '#dc3545',
                              backgroundColor: 'rgba(220, 53, 69, 0.12)',
                              pointerEvents: 'auto',
                              cursor: 'move',
                              boxSizing: 'border-box',
                            }}
                          >
                            <Box
                              data-bookmark-resize
                              onMouseDown={(e) => {
                                if (e.button !== 0) return;
                                e.stopPropagation();
                                e.preventDefault();
                                const overlay = e.currentTarget.closest('[data-page-overlay]');
                                if (!overlay) return;
                                setResizingBookmark({
                                  bookmark: b,
                                  startMouseX: e.clientX,
                                  startMouseY: e.clientY,
                                  startWidth: (b.width ?? 0),
                                  startHeight: (b.height ?? 0),
                                  pageRect: overlay.getBoundingClientRect(),
                                });
                                resizingBookmarkSizeRef.current = { width: b.width ?? 0, height: b.height ?? 0 };
                              }}
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                right: 0,
                                width: 18,
                                height: 18,
                                zIndex: 4,
                                cursor: 'nwse-resize',
                                borderLeft: '2px solid',
                                borderTop: '2px solid',
                                borderColor: '#dc3545',
                                bgcolor: 'rgba(255,255,255,0.9)',
                                boxSizing: 'border-box',
                              }}
                            />
                          </Box>
                        );
                      })}
                    {isDrawingThis && drawing && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: Math.min(drawing.startX, drawing.currentX),
                          top: Math.min(drawing.startY, drawing.currentY),
                          width: Math.abs(drawing.currentX - drawing.startX),
                          height: Math.abs(drawing.currentY - drawing.startY),
                          backgroundColor: (drawingBookmarkRegion || addingBookmarkHighlight) ? 'rgba(25, 118, 210, 0.3)' : 'rgba(255, 235, 59, 0.5)',
                          border: `2px dashed ${(drawingBookmarkRegion || addingBookmarkHighlight) ? '#1976d2' : '#333'}`,
                        }}
                      />
                    )}
                  </Box>
                    </>
                  ) : (
                    <Box
                      sx={{
                        minHeight: placeholderHeight,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'grey.100',
                        borderRadius: 0.5,
                        cursor: 'pointer',
                      }}
                      onClick={() => goToPage(pageNum)}
                    >
                      <Typography variant="body2" color="text.secondary">Page {pageNum}</Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Document>
        </Box>
        {/* Bottom floating bar – page navigation & zoom (like reference) */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: 'rgba(0,0,0,0.78)',
            color: 'white',
            px: 1.5,
            py: 0.75,
            borderRadius: 2,
            boxShadow: 2,
            zIndex: 5,
          }}
        >
          <Typography variant="body2" sx={{ mr: 0.5 }}>Page:</Typography>
          <IconButton size="small" sx={{ color: 'white' }} disabled={currentPage <= 1} onClick={() => goToPage(Math.max(1, currentPage - 1))}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'center' }}>{numPages ? `${currentPage}/${numPages}` : '—'}</Typography>
          <IconButton size="small" sx={{ color: 'white' }} disabled={currentPage >= numPages || numPages === 0} onClick={() => goToPage(Math.min(numPages, currentPage + 1))}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
          <Box sx={{ width: 1, height: 20, bgcolor: 'rgba(255,255,255,0.2)', mx: 0.5 }} />
          <Tooltip title="Zoom in">
            <IconButton size="small" sx={{ color: 'white' }} onClick={() => setZoom((z) => Math.min(2.5, z + 0.25))}><ZoomInIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Zoom out">
            <IconButton size="small" sx={{ color: 'white' }} onClick={() => setZoom((z) => Math.max(0.25, z - 0.25))}><ZoomOutIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Fit width">
            <IconButton size="small" sx={{ color: 'white' }} onClick={() => setZoom(0.85)}><FitScreenIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Fit page">
            <IconButton size="small" sx={{ color: 'white' }} onClick={() => setZoom(0.6)}><CropFreeIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
        </Box>

        {/* Right sidebar: small thumbnails (toggleable) */}
        {sidebarOpen && (
          <Paper
            elevation={0}
            sx={{
              order: 3,
              width: RIGHT_SIDEBAR_WIDTH,
              flexShrink: 0,
              overflow: 'hidden',
              borderLeft: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 0.5 }}>
              <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, px: 0.5 }}>Thumbnails</Typography>
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {pdfUrl && numPages > 0 && (
                  <Document file={pdfUrl}>
                    {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => {
                      const isCurrent = currentPage === pageNum;
                      return (
                        <Box
                          key={pageNum}
                          onClick={() => goToPage(pageNum)}
                          sx={{
                            cursor: 'pointer',
                            border: '2px solid',
                            borderColor: isCurrent ? '#dc3545' : 'transparent',
                            borderRadius: 0.5,
                            p: 0.25,
                            bgcolor: isCurrent ? 'rgba(220, 53, 69, 0.06)' : undefined,
                          }}
                        >
                          <Page pageNumber={pageNum} width={THUMB_WIDTH_SMALL} renderTextLayer={false} renderAnnotationLayer={false} />
                          <Typography variant="caption" sx={{ fontSize: 10, color: isCurrent ? '#dc3545' : 'text.secondary', fontWeight: isCurrent ? 600 : 400, display: 'block', textAlign: 'center' }}>
                            {pageNum}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Document>
                )}
              </Box>
            </Box>
          </Paper>
        )}
      </Box>

      {/* Add bookmark details (after highlighting area) */}
      <Dialog open={!!pendingNewBookmarkRegion} onClose={() => setPendingNewBookmarkRegion(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Add bookmark</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            You highlighted an area on page {pendingNewBookmarkRegion?.page_number}. Give this bookmark a title.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Bookmark title"
            placeholder="e.g. Section 3.2, References"
            value={bookmarkTitle}
            onChange={(e) => setBookmarkTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddBookmarkFromRegion()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPendingNewBookmarkRegion(null); setBookmarkTitle(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleAddBookmarkFromRegion}>Add bookmark</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editingBookmark} onClose={() => { setEditingBookmark(null); setDrawingBookmarkRegion(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Edit bookmark</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Title"
            value={bookmarkTitle}
            onChange={(e) => setBookmarkTitle(e.target.value)}
            sx={{ mt: 1 }}
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Target region (optional): click below then draw a rectangle on the page. When you click this bookmark, the PDF will scroll to that box.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                if (editingBookmark) {
                  setDrawingBookmarkRegion(editingBookmark);
                  setEditingBookmark(null);
                }
              }}
            >
              Set target region on page
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditingBookmark(null); setDrawingBookmarkRegion(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateBookmark}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentReview;
